// utils/publishTemplate.js
// ============================================================================
//  Publish a form as a template (organization- or global-scope) with an
//  optional “duplicate first” flow.
//
//  ⚙️  Flow when submitCopy === true
//  ────────────────────────────────
//  1. duplicateForm() clones the source *locally* and returns a new formId.
//     • It sets a local “unsaved copy” flag the Builder uses for its banner.
//  2. We read the entire source Firestore document (compressed payload,
//     screenshotUrl, etc.) and write it into the duplicate’s doc with setDoc().
//  3. We merge template metadata on top with updateDoc().
//  4. We CLEAR the local “unsaved copy” flag so the Configure screen opens
//     without the “You must save first” dialog.
//
//  ⚙️  Flow when submitCopy === false
//  ──────────────────────────────────
//  • We simply update the existing form doc in Firestore via updateDoc().
//
//  NOTE: If your Cloud Run screenshot service relies on a publish trigger
//  you’ll still get a *fresh* thumbnail later; cloning the old screenshot
//  just fills the placeholder immediately.
// ============================================================================

import {
  doc,
  getDoc,          // read full source document
  setDoc,          // write full clone for duplicate
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

import { db } from '../firebase';
import { duplicateForm } from './duplicateForm';
import { safeSetItem } from './storageUtils';

// 👉 NEW – reuse your existing helpers ------------------------------
import { decompressData }        from './decompressionUtils';
import { compressDataForDraft }  from './saveDraftFormToFirestore';
// -------------------------------------------------------------------

// ----------------------------------------------------------------------------
//  publishTemplate()
// ----------------------------------------------------------------------------
/**
 * Publishes a form as a template.
 *
 * @param {Object}   options
 * @param {string}   options.formId
 * @param {boolean} [options.submitCopy=false]
 * @param {string}  [options.copyTitle='']
 * @param {string}  [options.category='']
 * @param {string}  [options.templateScope='organization']  // or 'global'
 * @param {string}  [options.orgId='']
 * @param {Object}  [options.currentUser=null]
 * @param {Function}[options.initializeNewFormLocally=null] // required if submitCopy
 *
 * @returns {Promise<{status:'success'|'error', message:string, formId?:string}>}
 */
export async function publishTemplate({
  formId,
  submitCopy               = false,
  copyTitle                = '',
  category                 = '',
  templateScope            = 'organization',
  orgId                    = '',
  currentUser              = null,
  initializeNewFormLocally = null,
}) {
// ---------------------------------------------------------------------------
// 0) Debug logging (comment out if noisy)
// ---------------------------------------------------------------------------
// console.log('[publishTemplate] Starting publish with:', {
//   formId, submitCopy, copyTitle, category,
//   templateScope, orgId, currentUser,
//   initializeNewFormLocally: typeof initializeNewFormLocally
// });

// ---------------------------------------------------------------------------
// 1) Basic validation
// ---------------------------------------------------------------------------
  if (!formId) {
    console.warn('[publishTemplate] No formId provided; cannot publish.');
    return { status: 'error', message: 'Missing formId.' };
  }

// ---------------------------------------------------------------------------
// 2) Ensure we have an authenticated user
// ---------------------------------------------------------------------------
  if (!currentUser) {
    currentUser = getAuth().currentUser;
    if (!currentUser) {
      console.warn('[publishTemplate] Unable to resolve current user; proceeding without publishedBy info.');
    }
  }

  try {
    let finalFormId   = formId;
    let sourceDocData = null; // holds full payload when duplicating

// ---------------------------------------------------------------------------
// 3) Duplicate first, if requested
// ---------------------------------------------------------------------------
    if (submitCopy) {
      if (typeof initializeNewFormLocally !== 'function') {
        const msg = 'initializeNewFormLocally callback is required when submitCopy=true';
        console.error(`[publishTemplate] ${msg}`);
        return { status: 'error', message: msg };
      }

      console.log('[publishTemplate] submitCopy=TRUE → duplicating form before publish…');

      // 3a) Fetch the full source document so we can clone it
      const sourceSnap = await getDoc(doc(db, 'forms', formId));
      if (!sourceSnap.exists()) {
        console.error(`[publishTemplate] Source form "${formId}" not found in Firestore.`);
        return { status: 'error', message: `Source form ${formId} not found.` };
      }
      sourceDocData = sourceSnap.data();

      // 🆕───────────────────────────────────────────────────────────────
      // Patch the duplicate’s TITLE inside *both* places:
      //   • top-level `title`
      //   • inner `formDetails.title` inside compressedData blob
      // ─────────────────────────────────────────────────────────────────
      if (copyTitle) {
        // 1️⃣ top-level for good measure
        sourceDocData.title = copyTitle;
        if (sourceDocData.formDetails) {
          sourceDocData.formDetails = {
            ...sourceDocData.formDetails,
            title: copyTitle,
          };
        }

        // 2️⃣ inside compressedData (builder reads this path)
        if (sourceDocData.compressedData) {
          try {
            const draftObj = await decompressData(sourceDocData.compressedData);
            draftObj.formDetails = {
              ...(draftObj.formDetails || {}),
              title: copyTitle,
            };
            sourceDocData.compressedData = await compressDataForDraft(draftObj);
            console.log('[publishTemplate] copyTitle patched inside compressedData.');
          } catch (patchErr) {
            console.warn('[publishTemplate] Failed to patch compressedData title – proceeding anyway:', patchErr);
          }
        }
      }

      // 🆕 remove fields that don’t make sense on the duplicate
      ['unpublishedAt', 'unpublishedBy'].forEach(k => delete sourceDocData[k]);

      // 3b) Local duplicate → new formId + storage hydration
      const dupResult = await duplicateForm(
        formId,
        { currentUser, organizationId: orgId },
        initializeNewFormLocally,
      );

      if (dupResult.status !== 'success') {
        console.error('[publishTemplate] Duplicate failed:', dupResult.message);
        return { status: 'error', message: dupResult.message };
      }

      finalFormId = dupResult.newFormId || formId;
      console.log('[publishTemplate] Duplicate succeeded. newFormId:', finalFormId);
    }

// ---------------------------------------------------------------------------
// 4) Build the template metadata payload
// ---------------------------------------------------------------------------
    const templateMeta = {
      isTemplate       : true,
      templateScope,
      status           : 'published',
      templateCategory : category || null,
      publishedAt      : serverTimestamp(),
      lastModified     : serverTimestamp(),
    };

    if (currentUser?.uid) templateMeta.publishedBy = currentUser.uid;
    if (templateScope === 'organization' && orgId)
      templateMeta.organizationId = orgId;
    if (submitCopy && copyTitle)  templateMeta.title = copyTitle;

// ---------------------------------------------------------------------------
// 5) Write to Firestore
// ---------------------------------------------------------------------------
    const formRef = doc(db, 'forms', finalFormId);

    if (submitCopy) {
      // 5a) First write the full cloned payload
      await setDoc(formRef, sourceDocData);
      // 5b) Then merge the template metadata on top
      await updateDoc(formRef, templateMeta);
      console.log('[publishTemplate] Full clone + metadata written to new duplicate.');
    } else {
      // Existing doc → just update metadata
      await updateDoc(formRef, templateMeta);
      console.log('[publishTemplate] Metadata merged into existing form.');
    }

// ---------------------------------------------------------------------------
// 6) Clear the local “unsaved copy” flag so the Builder skips the banner
// ---------------------------------------------------------------------------
    if (submitCopy) {
      const modifiedKey = `form_${finalFormId}_isFormModified`;
      safeSetItem(modifiedKey, false);     // ⬅ overwrite any true flag
      sessionStorage.removeItem(modifiedKey);
      console.log('[publishTemplate] Cleared local unsaved-copy banner flag.');
    }

// ---------------------------------------------------------------------------
// 7) Done
// ---------------------------------------------------------------------------
    return {
      status : 'success',
      message: 'Form published as template successfully.',
      formId : finalFormId,
    };

  } catch (error) {
    console.error('[publishTemplate] Unexpected error:', error);
    return {
      status : 'error',
      message: error.message || 'Unexpected error during template publish.',
    };
  }
}
